import { CrossIcon, ShowIcon } from "../../assets/icons/iconsProvider";
import {
  EMPTY_STRING,
  globalConstants,
  STORE_BILLING_STATUS,
  routesPathConstants,
  BG_JOB_STATUS,
} from "../../constants";
import {
  ProgressBar,
  TableCustomHeader,
} from "../components";

import {
  calculateBarWidth,
  createCompanyDynamicUrl,
  formatedDateTime,
  getColumnStatusColumn,
} from "../utility";
import { isAdminLogin } from "./authenticationUtility";

const isStoreActive = (store, user, storeFetchingResponse = false) => {
  let response = false;
  const isAdmin = user && isAdminLogin(user);

  // initial state of store is undefined on reload so in case of routes we want to return true (so it stays on the same URL) and in case of dashboard we want to return EMPTY_STRING so it does not show store deactive text
  if (!store || store?.isStoreActive === undefined) {
    response = storeFetchingResponse;
  }
  // Admin user will have access to the store no matter what so no need to check payment status etc
  else if (isAdmin) {
    response = true;
  }
  // Payment is due or trial ended: disable access because of payment
  else if (
    store.billing?.storeBillingStatus === STORE_BILLING_STATUS.SERVICE_SUSPENDED
  ) {
    response = false;
  }
  // Admin user or store is active
  else if (store.isStoreActive) {
    response = true;
  }
  // Store is set to inactive by admin using store edit store.isStoreActive === false
  else {
  }

  return response;
};
const isStripeSubscriptionActive = (billing) => {
  const storeBilling = billing?.billing || billing || {};
  return (
    storeBilling.storeBillingStatus === STORE_BILLING_STATUS.ACTIVE_SUBSCRIPTION
  );
};

const getAllStoreIds = (companyStores = []) => {
  return companyStores?.map((store) => store.id).join(",") || EMPTY_STRING;
};

const createCompanyStoreOptions = (
  companyStores,
  showAllStoresOption = true
) => {
  const storeOptions = companyStores.map((store) => ({
    label: store.storeName,
    value: store.id,
  }));
  return [
    showAllStoresOption &&
      companyStores.length > 1 && {
        label: globalConstants.ALL_STORES,
        value: getAllStoreIds(companyStores),
      },
    ...storeOptions,
  ].filter(Boolean);
};

const getResetInventoryColumns = (props) => {
  const { handleViewStatus, handleCancelResetProgress } = props;
  return [
    {
      id: "startedAt",
      name: "Started at",
      selector: (row) => row.miscData?.startedAt,
      cell: (row) => {
        const timeText = formatedDateTime(row.miscData.startedAt);
        return row.miscData.startedAt ? (
          <TableCustomHeader
            id={`started-time-cell-${row.id}`}
            label={<span className="inventory-cell-max-size">{timeText}</span>}
            toolTipText={timeText}
          />
        ) : (
          <span>-</span>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "12%",
    },
    {
      id: "completedAt",
      name: "Completed at",
      selector: (row) => row.miscData?.completedAt,
      cell: (row) => {
        const timeText = formatedDateTime(row.miscData.completedAt);
        return row.miscData.completedAt ? (
          <TableCustomHeader
            id={`completed-time-cell-${row.id}`}
            label={<span className="inventory-cell-max-size">{timeText}</span>}
            toolTipText={timeText}
          />
        ) : (
          <span>-</span>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "12%",
    },
    {
      id: "scheduleTime",
      name: "Schedule Time",
      selector: (row) => row.miscData?.scheduleTime,
      cell: (row) => {
        const hasScheduleTime =
          row?.miscData?.scheduleTime &&
          row?.miscData?.status === BG_JOB_STATUS.PAUSED;
        if (!hasScheduleTime) return <span>-</span>;
        const timeText = formatedDateTime(row.miscData.scheduleTime);
        return (
          <TableCustomHeader
            id={`schedule-time-cell-${row.id}`}
            label={<span className="inventory-cell-max-size">{timeText}</span>}
            toolTipText={timeText}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "12%",
    },
    {
      id: "lastResumedAt",
      name: "Last Resumed At",
      selector: (row) => row.miscData?.lastResumedAt,
      cell: (row) => {
        const showLastResumedAt = row?.miscData?.lastResumedAt;
        if (!showLastResumedAt) return <span>-</span>;
        const timeText = formatedDateTime(row.miscData.lastResumedAt);
        return (
          <TableCustomHeader
            id={`last-resumed-at-cell-${row.id}`}
            label={<span className="inventory-cell-max-size">{timeText}</span>}
            toolTipText={timeText}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "12%",
    },
    {
      id: "lastPausedAt",
      name: "Last Paused At",
      selector: (row) => row.miscData?.lastPausedAt,
      cell: (row) => {
        const showLastPausedAt = row?.miscData?.lastPausedAt;
        if (!showLastPausedAt) return <span>-</span>;
        const timeText = formatedDateTime(row.miscData.lastPausedAt);
        return (
          <TableCustomHeader
            id={`last-paused-at-cell-${row.id}`}
            label={<span className="inventory-cell-max-size">{timeText}</span>}
            toolTipText={timeText}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "12%",
    },
    {
      id: "terminatedAt",
      name: "Terminated At",
      selector: (row) => row.miscData?.terminatedAt,
      cell: (row) => {
        const showTerminatedAt = row?.miscData?.terminatedAt;
        if (!showTerminatedAt) return <span>-</span>;
        const timeText = formatedDateTime(row.miscData.terminatedAt);
        return (
          <TableCustomHeader
            id={`terminated-at-cell-${row.id}`}
            label={<span className="inventory-cell-max-size">{timeText}</span>}
            toolTipText={timeText}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "12%",
    },
    getColumnStatusColumn((row) => row.miscData.status),
    {
      id: "progressPercentage",
      name: "Progress",
      selector: (row) => row?.miscData?.progressPercentage || 0,
      cell: (row) => {
        const progress = row?.miscData?.progressPercentage || 0;
        return (
          <div style={{ width: "100%" }}>
            <ProgressBar
              min={0}
              max={100}
              current={progress}
              percentage={calculateBarWidth(100, progress)}
              label={`${progress}%`}
              barWrapper={true}
            />
          </div>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "11%",
    },
    {
      id: "action",
      name: "Action",
      cell: (row) => {
        const isCancelDisabled =
          row?.miscData?.status === BG_JOB_STATUS.COMPLETED ||
          row?.miscData?.status === BG_JOB_STATUS.FAILED ||
          row?.miscData?.status === BG_JOB_STATUS.TERMINATED;
        return (
          <div className="d-flex gap-1">
            <button
              type="button"
              onClick={() => handleViewStatus(row)}
              className="action-Button-Wrapper view-order-icon"
            >
              <ShowIcon className="cursor-pointer" />
            </button>
            <button
              type="button"
              onClick={() => handleCancelResetProgress(row)}
              className={`action-Button-Wrapper view-order-icon`}
              disabled={isCancelDisabled}
            >
              <CrossIcon
                className={
                  isCancelDisabled ? "cursor-not-allowed" : "cursor-pointer"
                }
              />
            </button>
          </div>
        );
      },
      width: "7%",
      button: true,
    },
  ];
};
const handleEditStoreClick = (row, navigate) => {
  navigate(createCompanyDynamicUrl(routesPathConstants.ADMIN_STORES_EDIT), {
    state: { ...row },
  });
};

export {
  isStoreActive,
  handleEditStoreClick,
  isStripeSubscriptionActive,
  createCompanyStoreOptions,
  getAllStoreIds,
  getResetInventoryColumns,
};
